// Pulse AI CFO - v1.0.0
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, useEffect, useState } from 'react';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n/config';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { EntityProvider } from './contexts/EntityContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import PublicLayout from './components/PublicLayout';
import { trackPageView } from './lib/monitoring';
import { registerServiceWorker } from './lib/pwa';
import { lazyWithRetry } from './lib/lazyWithRetry';
import InstallPrompt from './components/InstallPrompt';

// Lazy load all page components for better code splitting with retry mechanism
const Landing = lazyWithRetry(() => import('./pages/Landing'));
const DashboardHome = lazyWithRetry(() => import('./pages/DashboardHome'));
const Transactions = lazyWithRetry(() => import('./pages/Transactions'));
const Invoices = lazyWithRetry(() => import('./pages/Invoices'));
const Bills = lazyWithRetry(() => import('./pages/Bills'));
const Chat = lazyWithRetry(() => import('./pages/Chat'));
// TEMPORARILY HIDDEN FOR MVP FOCUS - See docs/features/mvp-features-restoration.md to restore
// const Goals = lazyWithRetry(() => import('./pages/Goals'));
// const Budget = lazyWithRetry(() => import('./pages/Budget'));
// const Funding = lazyWithRetry(() => import('./pages/Funding'));
const Reports = lazyWithRetry(() => import('./pages/Reports'));
const WeeklyReports = lazyWithRetry(() => import('./pages/WeeklyReports'));
const WeeklyReportDetail = lazyWithRetry(() => import('./pages/WeeklyReportDetail'));
const Settings = lazyWithRetry(() => import('./pages/Settings'));
// TEMPORARILY HIDDEN FOR BETA - See docs/features/business-plan-restoration.md to restore
// const BusinessPlan = lazyWithRetry(() => import('./pages/BusinessPlan'));
// TEMPORARILY HIDDEN FOR MVP FOCUS - See docs/features/mvp-features-restoration.md to restore
// const ProductAnalytics = lazyWithRetry(() => import('./pages/ProductAnalytics'));
const Login = lazyWithRetry(() => import('./pages/Login'));
const Signup = lazyWithRetry(() => import('./pages/Signup'));
const ForgotPassword = lazyWithRetry(() => import('./pages/ForgotPassword'));
const ResetPassword = lazyWithRetry(() => import('./pages/ResetPassword'));
const Onboarding = lazyWithRetry(() => import('./pages/Onboarding'));
const Checkout = lazyWithRetry(() => import('./pages/Checkout'));
const More = lazyWithRetry(() => import('./pages/More'));
const PrivacyPolicy = lazyWithRetry(() => import('./pages/legal/PrivacyPolicy'));
const TermsOfService = lazyWithRetry(() => import('./pages/legal/TermsOfService'));
const SecurityPolicy = lazyWithRetry(() => import('./pages/legal/SecurityPolicy'));
const About = lazyWithRetry(() => import('./pages/About'));
const Contact = lazyWithRetry(() => import('./pages/Contact'));
const Partners = lazyWithRetry(() => import('./pages/Partners'));
const PartnerLogin = lazyWithRetry(() => import('./pages/PartnerLogin'));
const PartnerDashboard = lazyWithRetry(() => import('./pages/PartnerDashboard'));
const PartnerAdmin = lazyWithRetry(() => import('./pages/PartnerAdmin'));

// Loading fallback component — delays 150ms before showing to avoid flash-of-spinner
// on fast chunk loads. If the chunk resolves before 150ms, this never renders.
function PageLoader() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 150);
    return () => clearTimeout(timer);
  }, []);

  if (!visible) return null;

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-deep)' }}>
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 mb-4" style={{ borderColor: 'var(--cyan)' }}></div>
        <p style={{ color: 'var(--text-secondary)' }}>Loading...</p>
      </div>
    </div>
  );
}

// Smart root route - redirects based on auth state
function RootRoute() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  // Logged-out users see landing page
  if (!user) {
    return <Navigate to="/landing" replace />;
  }

  // Logged-in users see dashboard
  return <Navigate to="/dashboard" replace />;
}

// Capture ?ref= referral code from URL and store in localStorage
function ReferralCapture() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref && ref.trim()) {
      localStorage.setItem('pulse_referral_code', ref.trim());
      localStorage.setItem('pulse_referral_timestamp', Date.now().toString());
      // Clean ref param from URL without navigation
      params.delete('ref');
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);
  return null;
}

// Page view tracker component
function PageViewTracker() {
  useEffect(() => {
    // Track initial page view
    trackPageView(window.location.pathname);

    // Track subsequent navigation
    const handleLocationChange = () => {
      trackPageView(window.location.pathname);
    };

    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);

  return null;
}

function App() {
  // Register service worker for PWA functionality
  useEffect(() => {
    registerServiceWorker();
  }, []);

  // Auto-reload when a new service worker takes control so users always see the latest deploy
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handleControllerChange = () => window.location.reload();
    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
    return () => navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
  }, []);

  return (
    <I18nextProvider i18n={i18n}>
      <ThemeProvider>
        <BrowserRouter>
          <ReferralCapture />
          <PageViewTracker />
          <AuthProvider>
            <EntityProvider>
            <LanguageProvider>
              <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Smart root route */}
                <Route path="/" element={<RootRoute />} />

                {/* Public routes - wrapped in PublicLayout (light mode only) */}
                <Route element={<PublicLayout />}>
                  <Route path="/landing" element={<Landing />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/security" element={<SecurityPolicy />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/partners" element={<Partners />} />
                  <Route path="/partner-login" element={<PartnerLogin />} />
                  <Route path="/partner-dashboard" element={<PartnerDashboard />} />

                  {/* Onboarding route - protected but light mode only */}
                  <Route
                    path="/onboarding"
                    element={
                      <ProtectedRoute requireOnboarding={false}>
                        <Onboarding />
                      </ProtectedRoute>
                    }
                  />

                  {/* Checkout route - protected, requires onboarding, but light mode only */}
                  <Route
                    path="/checkout"
                    element={
                      <ProtectedRoute requireOnboarding={true} requireSubscription={false}>
                        <Checkout />
                      </ProtectedRoute>
                    }
                  />
                </Route>

                {/* Protected dashboard routes - with dark mode support */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<DashboardHome />} />
                  <Route path="transactions" element={<Transactions />} />
                  <Route path="invoices" element={<Invoices />} />
                  <Route path="bills" element={<Bills />} />
                  <Route path="chat" element={<Chat />} />
                  {/* TEMPORARILY HIDDEN FOR MVP FOCUS - See docs/features/mvp-features-restoration.md to restore */}
                  {/* <Route path="goals" element={<Goals />} /> */}
                  {/* <Route path="budget" element={<Budget />} /> */}
                  {/* <Route path="funding" element={<Funding />} /> */}
                  <Route path="reports" element={<Reports />} />
                  <Route path="insights" element={<Reports />} />
                  <Route path="weekly-reports" element={<WeeklyReports />} />
                  <Route path="weekly-reports/:id" element={<WeeklyReportDetail />} />
                  {/* TEMPORARILY HIDDEN FOR BETA - See docs/features/business-plan-restoration.md to restore */}
                  {/* <Route path="business-plan" element={<BusinessPlan />} /> */}
                  {/* TEMPORARILY HIDDEN FOR MVP FOCUS - See docs/features/mvp-features-restoration.md to restore */}
                  {/* <Route path="product-analytics" element={<ProductAnalytics />} /> */}
                  <Route path="more" element={<More />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="admin/partners" element={<PartnerAdmin />} />
                </Route>
              </Routes>
              </Suspense>
              {/* PWA Install Prompt */}
              <InstallPrompt />
            </LanguageProvider>
            </EntityProvider>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </I18nextProvider>
  );
}

export default App;
